import { ErrorLike } from '@apollo/client';
import { useApolloClient, useQuery } from '@apollo/client/react';
import { useRef, useState } from 'react';

import { Kitchen } from '../libs';
import { LIST_FOOD_COMPONENTS_BY_NAME_QUERY, LIST_INGREDIENTS_BY_NAME_QUERY } from '../libs/graphQL/queries';
import {
  TRACEABILITY_CHILD_LABELS_QUERY,
  TRACEABILITY_LABELS_QUERY,
  TRACEABILITY_LABEL_QUERY
} from '../libs/graphQL/traceability';
import { ItemOption, Label, LabelDetails, LabelPage } from '../views/Traceability/types';

const refetchQuietly = (refetch: () => Promise<unknown>) => async () => {
  await refetch();
};

interface ItemRow {
  id: string;
  name: { en: string };
  measurementUnit?: string | null;
}

const MODELED_UNITS = new Set(['g', 'ml', 'piece']);
const COMPONENT_PAGE_SIZE = 50;
export const ITEM_SEARCH_DELAY_MS = 250;

const toOptions = (rows: ItemRow[] | undefined): ItemOption[] =>
  (rows ?? []).flatMap(({ id, name, measurementUnit }) =>
    measurementUnit && MODELED_UNITS.has(measurementUnit) ? [{ id, name: name.en, unit: measurementUnit }] : []
  );

export interface UseTraceabilityItemOptionsParams {
  kitchen: Kitchen;
  /** The name to search for; the first page of each source only. */
  text: string;
}

/**
 * Items from `LIST_INGREDIENTS_BY_NAME_QUERY` and `LIST_FOOD_COMPONENTS_BY_NAME_QUERY` (`brand: 'CALO'`, `limit: 50`),
 * keeping the rows measured in g, ml or piece and showing `name.en`.
 */
export interface UseTraceabilityItemOptionsResult {
  items: ItemOption[];
  loading: boolean;
  error: ErrorLike | undefined;
}

export const useTraceabilityItemOptions = ({
  kitchen,
  text
}: UseTraceabilityItemOptionsParams): UseTraceabilityItemOptionsResult => {
  const ingredients = useQuery<{ listIngredientsByName: { data: ItemRow[] } }>(LIST_INGREDIENTS_BY_NAME_QUERY, {
    variables: { kitchen, name: text }
  });
  const components = useQuery<{ listFoodComponentsByName: { data: ItemRow[] } }>(LIST_FOOD_COMPONENTS_BY_NAME_QUERY, {
    variables: { kitchen, brand: 'CALO', name: text, limit: COMPONENT_PAGE_SIZE }
  });

  const items = [
    ...toOptions(ingredients.data?.listIngredientsByName.data),
    ...toOptions(components.data?.listFoodComponentsByName.data)
  ];
  return { items, loading: ingredients.loading || components.loading, error: ingredients.error ?? components.error };
};

const uniqById = (labels: Label[]): Label[] => [...new Map(labels.map((label) => [label.id, label])).values()];

export interface UseTraceabilityLabelsParams {
  kitchen: Kitchen;
  /** First day of the range, `yyyy-MM-dd` in the kitchen's zone. */
  from: string;
  /** Last day of the range, inclusive. */
  to: string;
  itemId?: string;
  limit?: number;
}

/** `listLabels`: the top-level labels, newest first; `loadMore` fetches the next page with the cursor and appends it to `labels`. */
export interface UseTraceabilityLabelsResult {
  labels: Label[];
  loading: boolean;
  error: ErrorLike | undefined;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  refetch: () => Promise<void>;
}

export const useTraceabilityLabels = (params: UseTraceabilityLabelsParams): UseTraceabilityLabelsResult => {
  const client = useApolloClient();
  const first = useQuery<{ listLabels: LabelPage }>(TRACEABILITY_LABELS_QUERY, {
    variables: params,
    fetchPolicy: 'cache-and-network'
  });
  const firstPage = first.data?.listLabels;
  const [more, setMore] = useState<{ after: LabelPage | undefined; pages: LabelPage[] }>({ after: undefined, pages: [] });
  const loading = useRef<Promise<void> | undefined>(undefined);

  // Pages fetched after a first page belong to that page only; a new first page starts the list again.
  const pages = more.after === firstPage ? more.pages : [];
  const cursor = (pages.at(-1) ?? firstPage)?.next ?? undefined;
  const labels = uniqById([firstPage, ...pages].flatMap((page) => page?.items ?? []));

  const loadMore = async () => {
    if (!cursor || !firstPage) return;
    loading.current ??= client
      .query<{ listLabels: LabelPage }>({
        query: TRACEABILITY_LABELS_QUERY,
        variables: { ...params, next: cursor },
        fetchPolicy: 'network-only'
      })
      .then(({ data }) => {
        setMore((current) => ({
          after: firstPage,
          pages: [...(current.after === firstPage ? current.pages : []), data!.listLabels]
        }));
      })
      .finally(() => {
        loading.current = undefined;
      });
    await loading.current;
  };

  const refetch = async () => {
    setMore({ after: undefined, pages: [] });
    await first.refetch();
  };

  return {
    labels,
    loading: first.loading && !firstPage,
    error: first.error,
    hasMore: cursor !== undefined,
    loadMore,
    refetch
  };
};

export interface UseTraceabilityChildLabelsParams {
  kitchen: Kitchen;
  parentId: string;
}

/** `listChildLabels`: the direct children of `parentId`, oldest first. A mount over a cached list shows it and asks the network again. */
export interface UseTraceabilityChildLabelsResult {
  labels: Label[];
  loading: boolean;
  error: ErrorLike | undefined;
  refetch: () => Promise<void>;
}

export const useTraceabilityChildLabels = ({
  kitchen,
  parentId
}: UseTraceabilityChildLabelsParams): UseTraceabilityChildLabelsResult => {
  const query = useQuery<{ listChildLabels: Label[] }>(TRACEABILITY_CHILD_LABELS_QUERY, {
    variables: { kitchen, parentId },
    fetchPolicy: 'cache-and-network'
  });
  return {
    labels: query.data?.listChildLabels ?? [],
    loading: query.loading,
    error: query.error,
    refetch: refetchQuietly(query.refetch)
  };
};

export interface UseTraceabilityLabelParams {
  kitchen: Kitchen;
  /** The route's label ID; the query is skipped while it is undefined. */
  id: string | undefined;
}

/** `getLabel` for the route's label ID. A failure keeps its AppSync `errorType` (LabelNotFound for an unregistered ID). */
export interface UseTraceabilityLabelResult {
  details: LabelDetails | undefined;
  loading: boolean;
  error: ErrorLike | undefined;
  refetch: () => Promise<void>;
}

export const useTraceabilityLabel = ({ kitchen, id }: UseTraceabilityLabelParams): UseTraceabilityLabelResult => {
  const query = useQuery<{ getLabel: LabelDetails }>(TRACEABILITY_LABEL_QUERY, {
    variables: { kitchen, id },
    skip: id === undefined,
    fetchPolicy: 'cache-and-network'
  });
  return {
    details: query.data?.getLabel,
    loading: query.loading,
    error: query.error,
    refetch: refetchQuietly(query.refetch)
  };
};
